/** Matches profile / patients (customer): near-black stroke on the 500x500 Lottie canvas. */
export const DEFAULT_LOTTIE_COLOR: [number, number, number, number] = [0.047, 0.047, 0.047, 1];
/** Slate-100. Used when the app is in dark mode and the caller did not set a colour. */
export const DARK_LOTTIE_COLOR_HEX = "#f1f5f9";

/** `currentColor` cannot be painted into Lottie JSON, so dark mode gets an explicit light stroke. */
export function resolveLottieColorInput(color: string | undefined, isDark: boolean): string | undefined {
    if (color && color !== "currentColor") {
        return color;
    }

    return isDark ? DARK_LOTTIE_COLOR_HEX : undefined;
}
export const DEFAULT_LOTTIE_STROKE_WIDTH = 24;

const LUCIDE_STYLE_STROKE_MAX = 4;

export function parseLottieColor(color?: string): [number, number, number, number] {
    if (!color || color === "currentColor") {
        return DEFAULT_LOTTIE_COLOR;
    }

    if (color.startsWith("#")) {
        const hex = color.slice(1);
        const full =
            hex.length === 3
                ? hex
                      .split("")
                      .map((part) => `${part}${part}`)
                      .join("")
                : hex;
        const value = Number.parseInt(full, 16);
        if (Number.isNaN(value)) {
            return DEFAULT_LOTTIE_COLOR;
        }

        return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255, 1];
    }

    return DEFAULT_LOTTIE_COLOR;
}

/** Nav clones Lucide-style strokeWidth (1.5) onto Lottie icons; keep those at the profile weight. */
export function resolveLottieStrokeWidth(strokeWidth?: number): number {
    if (strokeWidth == null || strokeWidth <= LUCIDE_STYLE_STROKE_MAX) {
        return DEFAULT_LOTTIE_STROKE_WIDTH;
    }

    return strokeWidth;
}

function isRgba(value: unknown): value is number[] {
    return Array.isArray(value) && value.length >= 3 && typeof value[0] === "number" && value[0] <= 1.01;
}

function isRedMatte(rgba: number[]): boolean {
    return rgba[0] > 0.95 && rgba[1] < 0.08 && rgba[2] < 0.08;
}

function applyColor(target: number[], color: [number, number, number, number]): void {
    target[0] = color[0];
    target[1] = color[1];
    target[2] = color[2];
    if (target.length > 3) {
        target[3] = color[3];
    }
}

function walk(node: unknown, color: [number, number, number, number], strokeWidth: number): void {
    if (!node || typeof node !== "object") {
        return;
    }

    if (Array.isArray(node)) {
        for (const child of node) {
            walk(child, color, strokeWidth);
        }
        return;
    }

    const item = node as Record<string, unknown>;

    if (item.ty === "st" || item.ty === "fl") {
        const paint = item.c as { k?: unknown } | undefined;
        if (paint && isRgba(paint.k) && !isRedMatte(paint.k)) {
            applyColor(paint.k, color);
        }
    }

    if (item.ty === "st") {
        const width = item.w as { k?: unknown } | undefined;
        if (width && typeof width.k === "number" && width.k > 0) {
            width.k = strokeWidth;
        }
    }

    if (item.mn === "ADBE Color Control-0001") {
        const value = item.v as { k?: unknown } | undefined;
        if (value && isRgba(value.k) && !isRedMatte(value.k)) {
            applyColor(value.k, color);
        }
    }

    if (item.mn === "ADBE Slider Control" && typeof item.nm === "string" && /stroke width/i.test(item.nm)) {
        const effects = item.ef;
        if (Array.isArray(effects)) {
            for (const effect of effects) {
                walk(effect, color, strokeWidth);
                if (!effect || typeof effect !== "object") {
                    continue;
                }
                const slider = effect as Record<string, unknown>;
                if (slider.mn === "ADBE Slider Control-0001") {
                    const value = slider.v as { k?: unknown } | undefined;
                    if (value && typeof value.k === "number") {
                        value.k = strokeWidth;
                    }
                }
            }
            return;
        }
    }

    for (const child of Object.values(item)) {
        walk(child, color, strokeWidth);
    }
}

export function normalizeLottieAnimation(
    data: unknown,
    color: [number, number, number, number] = DEFAULT_LOTTIE_COLOR,
    strokeWidth: number = DEFAULT_LOTTIE_STROKE_WIDTH
): unknown {
    const clone = JSON.parse(JSON.stringify(data)) as unknown;
    walk(clone, color, strokeWidth);
    return clone;
}
